import { Box, Typography, Chip, TextField, Autocomplete } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Controller, FieldErrors, FieldValues, Control, Path } from 'react-hook-form'

import { MedicalActTemplate } from '@/core/models/medical-act.model'
import { formatPrice } from '@/utils/formatPrice'

export type TemplateAutocompleteProps<T extends FieldValues = FieldValues> = {
    templates: MedicalActTemplate[]
    control: Control<T>
    errors: FieldErrors
    name?: Path<T>
}

export function TemplateAutocomplete<T extends FieldValues = FieldValues>({
    templates,
    control,
    errors,
    name = 'templateId' as Path<T>,
}: TemplateAutocompleteProps<T>) {
    const { t } = useTranslation()

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: t('common.validation.required', { defaultValue: 'This field is required' }) }}
            render={({ field }) => (
                <Autocomplete
                    options={templates}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    value={templates.find(t => t._id === field.value) || null}
                    onChange={(_, newValue) => field.onChange(newValue?._id || '')}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('medicalAct.record.selectAct', { defaultValue: 'Select Medical Act' })}
                            required
                            error={!!errors[name]}
                            helperText={errors[name]?.message as string}
                        />
                    )}
                    renderOption={(props, option) => (
                        <Box component="li" {...props}>
                            <Box sx={{ width: '100%' }}>
                                <Typography variant="body1">{option.name}</Typography>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {option.code}
                                    </Typography>
                                    <Chip
                                        label={formatPrice(option.price)}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                </Box>
                            </Box>
                        </Box>
                    )}
                />
            )}
        />
    )
}
